import historiaBannerImage from "./assets/images/historia-banner.jpg";
import mapaNefastosImage from "./assets/images/mapa-nefastos.png";
import patchCompletoImage from "./assets/images/patch-completo.png";
import type { Locale } from "./i18n";

export type MediaType = "fotos" | "videos";

export type MediaItem = {
  id: string;
  type: MediaType;
  title: string;
  description: string;
  thumbnail: string;
  href: string;
};

type LocalizedContent = {
  seo: {
    defaultTitle: string;
    defaultDescription: string;
  };
  nav: Array<{ to: string; label: string }>;
  languageSwitcherLabel: string;
  hero: {
    eyebrow: string;
    title: string;
    kicker: string;
    description: string;
    ctaLabel: string;
    ctaHref: string;
    image: string;
    imageAlt: string;
  };
  story: {
    title: string;
    image: string;
    imageAlt: string;
    paragraphs: string[];
  };
  map: {
    title: string;
    region: string;
    image: string;
    imageAlt: string;
    locations: Array<{ name: string; url: string }>;
  };
  contact: {
    title: string;
    description: string;
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    submitLabel: string;
    consentLabel: string;
    sendingLabel: string;
    successMessage: string;
    errorMessage: string;
  };
  media: {
    title: string;
    subtitle: string;
    photosLabel: string;
    videosLabel: string;
    openLabel: string;
    emptyLabel: string;
    loadMoreLabel: string;
  };
  cookieConsent: {
    message: string;
    button: string;
  };
  privacy: {
    description?: string;
    title: string;
    version: string;
    sections: Array<{
      title: string;
      content: string;
    }>;
  };
  social: Array<{ href: string; label: string }>;
  footer: string;
};

const baseMedia: Record<Locale, MediaItem[]> = {
  pt: [
    {
      id: "foto-patch",
      type: "fotos",
      title: "Patch Oficial",
      description: "Identidade visual e tradição do Nefastos MC.",
      thumbnail: patchCompletoImage,
      href: patchCompletoImage,
    },
    {
      id: "foto-encontro",
      type: "fotos",
      title: "Encontro de Irmãos",
      description: "Registros de eventos e união entre os associados.",
      thumbnail: historiaBannerImage,
      href: historiaBannerImage,
    },
    {
      id: "foto-mapa",
      type: "fotos",
      title: "Mapa Nefastos",
      description: "Presença da irmandade na Bahia.",
      thumbnail: mapaNefastosImage,
      href: mapaNefastosImage,
    },
    {
      id: "video-canal",
      type: "videos",
      title: "Canal Oficial no YouTube",
      description: "Vídeos e cobertura de eventos do Nefastos MC.",
      thumbnail: patchCompletoImage,
      href: "https://www.youtube.com/channel/UCwM1rv-TfIumutNSmYna5JA/featured",
    },
    {
      id: "video-cobertura",
      type: "videos",
      title: "Cobertura de Eventos",
      description: "Conteúdos de estrada, encontros e confraternizações.",
      thumbnail: historiaBannerImage,
      href: "https://www.youtube.com/channel/UCwM1rv-TfIumutNSmYna5JA/videos",
    },
  ],
  en: [
    {
      id: "photo-patch",
      type: "fotos",
      title: "Official Patch",
      description: "Nefastos MC identity and traditional symbols.",
      thumbnail: patchCompletoImage,
      href: patchCompletoImage,
    },
    {
      id: "photo-brothers",
      type: "fotos",
      title: "Brothers Gathering",
      description: "Event moments and club brotherhood records.",
      thumbnail: historiaBannerImage,
      href: historiaBannerImage,
    },
    {
      id: "photo-map",
      type: "fotos",
      title: "Nefastos Map",
      description: "Brotherhood presence across Bahia.",
      thumbnail: mapaNefastosImage,
      href: mapaNefastosImage,
    },
    {
      id: "video-channel",
      type: "videos",
      title: "Official YouTube Channel",
      description: "Videos and event coverage from Nefastos MC.",
      thumbnail: patchCompletoImage,
      href: "https://www.youtube.com/channel/UCwM1rv-TfIumutNSmYna5JA/featured",
    },
    {
      id: "video-events",
      type: "videos",
      title: "Event Coverage",
      description: "Road content, meetings and biker gatherings.",
      thumbnail: historiaBannerImage,
      href: "https://www.youtube.com/channel/UCwM1rv-TfIumutNSmYna5JA/videos",
    },
  ],
};

export const localizedContent: Record<Locale, LocalizedContent> = {
  pt: {
    seo: {
      defaultTitle: "Nefastos MC Brasil",
      defaultDescription:
        "Nefastos MC Brasil: irmandade biker em Salvador/BA, eventos, história, mapa e conteúdo de mídia.",
    },
    languageSwitcherLabel: "Idioma",
    nav: [
      { to: "/", label: "INÍCIO" },
      { to: "/historia", label: "HISTÓRIA" },
      { to: "/mapa", label: "MAPA" },
      { to: "/contato", label: "CONTATO" },
      { to: "/midia/fotos", label: "FOTOS" },
      { to: "/midia/videos", label: "VÍDEOS" },
    ],
    hero: {
      eyebrow: "MOTO CLUBE • SALVADOR/BA",
      title: "NEFASTOS MC BRASIL",
      kicker: "",
      description:
        "Motoclube Soteropolitano fundado em 2017",
      ctaLabel: "FALE CONOSCO",
      ctaHref: "/contato",
      image: patchCompletoImage,
      imageAlt: "Patch oficial Nefastos MC",
    },
    story: {
      title: "História",
      image: historiaBannerImage,
      imageAlt: "Membros do Nefastos MC",
      paragraphs: [
        "Fundado em 2017, o Nefastos M.C. nasceu em Salvador, Bahia, seguindo a linha tradicional da cultura biker e fortalecendo valores de lealdade, honra e união entre motociclistas.",
        "O clube participa ativamente de eventos em todo o território nacional e mantém viva a fraternidade entre seus associados, independente de cilindrada, profissão ou origem.",
      ],
    },
    map: {
      title: "Mapa",
      region: "Bahia",
      image: mapaNefastosImage,
      imageAlt: "Mapa Nefastos MC",
      locations: [
        {
          name: "SALVADOR — SEDE ORIGINAL",
          url: "https://maps.app.goo.gl/SreLBKH5k95iD2KT7"
        }
      ],
    },
    contact: {
      title: "Contato",
      description: "Deixe-nos uma mensagem e retornaremos o contato o mais breve possível.",
      nameLabel: "Nome",
      namePlaceholder: "Nome",
      emailLabel: "E-mail",
      emailPlaceholder: "E-mail",
      phoneLabel: "Telefone",
      phonePlaceholder: "Telefone (ex.: 71997654321)",
      messageLabel: "Mensagem",
      messagePlaceholder: "Mensagem",
      submitLabel: "Enviar",
      consentLabel: "Eu aceito que meus dados sejam processados para fins de contato, conforme os termos de privacidade.",
      sendingLabel: "Enviando...",
      successMessage: "Mensagem enviada com sucesso.",
      errorMessage: "Não foi possível enviar agora. Tente novamente.",
    },
    media: {
      title: "Mídia Nefastos",
      subtitle:
        "Páginas dinâmicas com registros visuais da irmandade: fotos de encontros e vídeos do canal oficial.",
      photosLabel: "Fotos",
      videosLabel: "Vídeos",
      openLabel: "Abrir",
      emptyLabel: "Nenhum item disponível nesta categoria.",
      loadMoreLabel: "Carregar mais",
    },
    cookieConsent: {
      message: "Este site utiliza cookies para melhorar sua experiência e analisar nosso tráfego. Ao continuar navegando, você concorda com nossa política de privacidade e termos de uso.",
      button: "Aceitar e Continuar",
    },
    privacy: {
      title: "Política de Privacidade",
      version: "1.0.0",
      sections: [
        {
          title: "1. Coleta de Dados",
          content: "Coletamos informações básicas fornecidas voluntariamente por você através do formulário de contato (Nome, E-mail e Telefone), além de metadados técnicos (Endereço IP, tipo de navegador, resolução de tela e sistema operacional) para fins de segurança e melhoria da experiência do usuário."
        },
        {
          title: "2. Finalidade do Tratamento",
          content: "Os dados coletados são utilizados exclusivamente para responder às suas solicitações de contato, prevenir spam e ataques maliciosos ao nosso sistema, e analisar o tráfego de forma agregada."
        },
        {
          title: "3. Cookies e Rastreamento",
          content: "Utilizamos cookies essenciais para o funcionamento do site e cookies analíticos que nos ajudam a entender como os visitantes interagem com as páginas. Você pode gerenciar suas preferências de cookies através das configurações do seu navegador."
        },
        {
          title: "4. Seus Direitos (LGPD)",
          content: "De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem o direito de confirmar a existência do tratamento, acessar seus dados, corrigir informações incompletas ou inexatas e solicitar a exclusão de seus dados pessoais a qualquer momento através de nossos canais de contato."
        },
        {
          title: "5. Segurança",
          content: "Implementamos medidas técnicas e administrativas para proteger seus dados contra acessos não autorizados e situações acidentais ou ilícitas de destruição, perda ou alteração."
        }
      ]
    },
    social: [
      { href: "https://www.facebook.com/nefastosmc", label: "Facebook" },
      { href: "https://www.instagram.com/nefastosmc/?hl=pt-br", label: "Instagram" },
      { href: "https://www.youtube.com/@nefastosmotoclube569/videos", label: "YouTube" },
    ],
    footer: "© 2026 POR NEFASTOS MC, TODOS OS DIREITOS RESERVADOS.",
  },
  en: {
    seo: {
      defaultTitle: "Nefastos MC Brazil",
      defaultDescription:
        "Nefastos MC Brazil: biker brotherhood from Salvador/BA with history, map, contact and dynamic media pages.",
    },
    languageSwitcherLabel: "Language",
    nav: [
      { to: "/", label: "HOME" },
      { to: "/historia", label: "HISTORY" },
      { to: "/mapa", label: "MAP" },
      { to: "/contato", label: "CONTACT" },
      { to: "/midia/fotos", label: "PHOTOS" },
      { to: "/midia/videos", label: "VIDEOS" },
    ],
    hero: {
      eyebrow: "MOTORCYCLE CLUB • SALVADOR/BA",
      title: "NEFASTOS MC BRAZIL",
      kicker: "",
      description:
        "Motorcycle club from Salvador founded in 2017.",
      ctaLabel: "CONTACT US",
      ctaHref: "/contato",
      image: patchCompletoImage,
      imageAlt: "Nefastos MC official patch",
    },
    story: {
      title: "History",
      image: historiaBannerImage,
      imageAlt: "Nefastos MC members",
      paragraphs: [
        "Founded in 2017, Nefastos M.C. was born in Salvador, Bahia, following traditional biker culture and strengthening values of loyalty, honor and brotherhood.",
        "The club actively joins events across Brazil and keeps fraternity alive among members, regardless of bike size, profession or origin.",
      ],
    },
    map: {
      title: "Map",
      region: "Bahia",
      image: mapaNefastosImage,
      imageAlt: "Nefastos MC map",
      locations: [
        {
          name: "SALVADOR — ORIGINAL HEADQUARTERS",
          url: "https://maps.app.goo.gl/SreLBKH5k95iD2KT7"
        }
      ],
    },
    contact: {
      title: "Contact",
      description: "Leave us a message and we will contact you as soon as possible.",
      nameLabel: "Name",
      namePlaceholder: "Name",
      emailLabel: "Email",
      emailPlaceholder: "Email",
      phoneLabel: "Phone",
      phonePlaceholder: "Phone (e.g. 71997654321)",
      messageLabel: "Message",
      messagePlaceholder: "Message",
      submitLabel: "Send",
      consentLabel: "I agree to have my data processed for contact purposes, according to the privacy terms.",
      sendingLabel: "Sending...",
      successMessage: "Message sent successfully.",
      errorMessage: "We could not send your message right now. Please try again.",
    },
    media: {
      title: "Nefastos Media",
      subtitle:
        "Dynamic pages with visual records of the brotherhood: events photos and videos from the official channel.",
      photosLabel: "Photos",
      videosLabel: "Videos",
      openLabel: "Open",
      emptyLabel: "No items available in this category.",
      loadMoreLabel: "Load more",
    },
    cookieConsent: {
      message: "This site uses cookies to improve your experience and analyze our traffic. By continuing to browse, you agree to our privacy policy and terms of use.",
      button: "Accept and Continue",
    },
    privacy: {
      title: "Privacy Policy",
      version: "1.0.0",
      sections: [
        {
          title: "1. Data Collection",
          content: "We collect basic information voluntarily provided by you through the contact form (Name, Email and Phone), as well as technical metadata (IP address, browser type, screen resolution and operating system) for security purposes and user experience improvement."
        },
        {
          title: "2. Purpose of Processing",
          content: "Collected data is used exclusively to respond to your contact requests, prevent spam and malicious attacks on our system, and analyze aggregate traffic."
        },
        {
          title: "3. Cookies and Tracking",
          content: "We use essential cookies for the site's operation and analytical cookies that help us understand how visitors interact with the pages. You can manage your cookie preferences through your browser settings."
        },
        {
          title: "4. Your Rights (GDPR/LGPD)",
          content: "In accordance with General Data Protection Regulations, you have the right to confirm processing existence, access your data, correct incomplete or inaccurate information, and request your personal data exclusion at any time through our contact channels."
        },
        {
          title: "5. Security",
          content: "We implement technical and administrative measures to protect your data against unauthorized access and accidental or unlawful destruction, loss or alteration situations."
        }
      ]
    },
    social: [
      { href: "https://www.facebook.com/nefastosmc", label: "Facebook" },
      { href: "https://www.instagram.com/nefastosmc/?hl=pt-br", label: "Instagram" },
      { href: "https://www.youtube.com/@nefastosmotoclube569/videos", label: "YouTube" },
    ],
    footer: "© 2026 BY NEFASTOS MC, ALL RIGHTS RESERVED.",
  },
};

export function getContentByLocale(locale: Locale) {
  return localizedContent[locale];
}

export function getMediaByLocale(locale: Locale) {
  return baseMedia[locale];
}
